import { useCallback, useEffect, useState } from 'react';

const MAX_QUANTITY = 1000;
const EVENT = 'ibigdo-cart';
const keyFor = (slug) => `ibigdo.cart.${String(slug || '').toLowerCase()}`;

function read(slug) {
  try {
    const items = JSON.parse(localStorage.getItem(keyFor(slug)) || '[]');
    return Array.isArray(items)
      ? items.filter((i) => Number.isInteger(i?.productId) && Number.isInteger(i?.quantity) && i.quantity > 0).map((i) => ({ productId: i.productId, quantity: Math.min(i.quantity, MAX_QUANTITY) }))
      : [];
  } catch (e) {
    return [];
  }
}

function write(slug, items) {
  try {
    localStorage.setItem(keyFor(slug), JSON.stringify(items));
  } catch (e) {
    // a browser that keeps nothing still shops, but the cart is lost on leaving the page
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: keyFor(slug) }));
}

/** A buyer's cart for one store: product ids and quantities only, kept in the browser. Prices always come from the server. */
export default function useCart(slug) {
  const [items, setItems] = useState(() => read(slug));

  useEffect(() => {
    setItems(read(slug));
    const sync = (e) => { if (!e.detail || e.detail === keyFor(slug)) setItems(read(slug)); };
    const storage = (e) => { if (e.key === keyFor(slug)) setItems(read(slug)); };
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', storage);
    return () => { window.removeEventListener(EVENT, sync); window.removeEventListener('storage', storage); };
  }, [slug]);

  const add = useCallback((productId, quantity = 1) => {
    const current = read(slug);
    const found = current.find((i) => i.productId === productId);
    if (found) found.quantity = Math.min(found.quantity + quantity, MAX_QUANTITY);
    else current.push({ productId, quantity: Math.min(quantity, MAX_QUANTITY) });
    write(slug, current);
  }, [slug]);

  const setQuantity = useCallback((productId, quantity) => {
    const q = Math.max(1, Math.min(Math.floor(Number(quantity)) || 1, MAX_QUANTITY));
    write(slug, read(slug).map((i) => (i.productId === productId ? { ...i, quantity: q } : i)));
  }, [slug]);

  const remove = useCallback((productId) => write(slug, read(slug).filter((i) => i.productId !== productId)), [slug]);
  const clear = useCallback(() => write(slug, []), [slug]);

  return { items, count: items.reduce((n, i) => n + i.quantity, 0), add, setQuantity, remove, clear };
}
